import { z } from "zod";

export const REFUND_STATUSES = ["REQUESTED", "PROCESSING", "COMPLETED", "FAILED"] as const;
export const REFUND_METHODS = ["PG_CANCEL", "BANK_TRANSFER"] as const;

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidRefundDate(value: string): boolean {
  if (!value) return true;
  const match = isoDatePattern.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 9998) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export const refundSearchFormSchema = z.object({
  status: z.union([z.literal(""), z.enum(REFUND_STATUSES)]),
  method: z.union([z.literal(""), z.enum(REFUND_METHODS)]),
  memberId: z.string().refine(
    (value) => {
      const normalized = value.trim();
      return normalized === "" || (/^\d+$/.test(normalized)
        && Number.isSafeInteger(Number(normalized))
        && Number(normalized) > 0);
    },
    "회원 ID는 1 이상의 숫자로 입력해 주세요.",
  ),
  from: z.string().refine(isValidRefundDate, "유효한 요청 시작일을 선택해 주세요."),
  to: z.string().refine(isValidRefundDate, "유효한 요청 종료일을 선택해 주세요."),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) {
    context.addIssue({
      code: "custom",
      message: "요청 종료일은 시작일보다 빠를 수 없습니다.",
      path: ["to"],
    });
  }
});

export type RefundSearchFormValues = z.infer<typeof refundSearchFormSchema>;

export function parseRefundStatus(value: string | null): RefundSearchFormValues["status"] {
  if (!value || value === "ALL") return "";
  const parsed = z.enum(REFUND_STATUSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseRefundMethod(value: string | null): RefundSearchFormValues["method"] {
  if (!value || value === "ALL") return "";
  const parsed = z.enum(REFUND_METHODS).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseRefundMemberId(value: string | null): string {
  if (!value || !/^\d+$/.test(value)) return "";
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : "";
}

export function parseRefundDate(value: string | null): string {
  return value && isValidRefundDate(value) ? value : "";
}
